import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { getEvvServiceUnit } from '@/_helpers';

export const changeVisitLabelFromResponse = (response) => {
    if (response?.label) {
        toast.success(`Label changed to ${response.label}`);
    }
    if (response?.newAvailableUnits) {
        toast.success(`Client units has been deducted. New available units is ${response?.newAvailableUnits}`);
    } else if (response?.addedUnitsEntry && response?.removedUnitsEntry) {
        if (response?.addedUnitsEntry?.expiry === response?.removedUnitsEntry?.expiry) {
            toast.success(
                `Client units ${response?.removedUnitsEntry?.amount} has been updated to ${
                    response?.addedUnitsEntry?.amount
                } & expires on ${dayjs(response?.addedUnitsEntry?.expiry).format('MMM DD YYYY')}`
            );
        }
    } else if (response?.addedUnitsEntry) {
        toast.success(
            `client units ${response?.addedUnitsEntry?.amount} which expires on  ${dayjs(
                response?.addedUnitsEntry?.expiry
            ).format('MMM DD YYYY')} has been added.`
        );
    }
};
export const getVisitUnitsAndAmount = (visit) => {
    const billableStatus = (visit?.extraInfo?.BillVisit ?? true).toString() === 'true';
    const visitTime = dayjs(visit?.ScheduleEndTime).diff(dayjs(visit?.ScheduleStartTime), 'm');
    const minutesPerUnit = visit?.extraInfo?.minutesPerUnit || 15;
    const billUnit =
        (billableStatus && (visit?.extraInfo?.unitsConsumed || getEvvServiceUnit(visitTime, minutesPerUnit))) || 0;
    const staffRate = visit?.extraInfo?.staffRate || 0;
    const serviceRate = visit?.extraInfo?.serviceRate || 0;
    const staffAmount = parseFloat(visit?.extraInfo?.staffAmount || 0)?.toFixed(2);
    const serviceAmount = parseFloat(serviceRate * billUnit).toFixed(2);
    return {
        billUnit,
        serviceAmount,
        staffAmount,
        staffRate,
        serviceRate,
    };
};

export const getServiceCode = (visit) => visit?.extraInfo?.ProcedureCode;
export const getCombinedModifier = (visit) =>
    `${visit?.extraInfo?.Modifier1 || '-'}${visit?.extraInfo?.Modifier2 || '-'}${visit?.extraInfo?.Modifier3 || '-'}${visit?.extraInfo?.Modifier4 || '-'}`.replaceAll(
        ' ',
        ''
    );

// Helper: check if two visits have overlapping time ranges by more than 14 minutes.
// 14-minute tolerance accounts for clock rounding: staff clocking out late (rounds down)
// and the next staff clocking in early (rounds up) can show up to ~16 min raw overlap
// that resolves to zero after rounding. Only overlap > 14 min is treated as a true conflict.
const timesOverlap = (a, b) => {
    const aStart = dayjs(a?.ScheduleStartTime).valueOf();
    const aEnd = dayjs(a?.ScheduleEndTime).valueOf();
    const bStart = dayjs(b?.ScheduleStartTime).valueOf();
    const bEnd = dayjs(b?.ScheduleEndTime).valueOf();
    // Visits can overlap by up to 14 minutes and still be combined
    const overlapMs = Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
    const overlapMinutes = overlapMs / 60000;
    return overlapMinutes > 14;
};

export const buildCombinedVisitSummary = (groupVisits) => {
    const firstVisit = groupVisits?.[0];
    let earliestStart = firstVisit?.ScheduleStartTime || null;
    let latestEnd = firstVisit?.ScheduleEndTime || null;
    let totalMinutes = 0;
    let totalBillableMinutes = 0;
    let totalUnits = 0;
    let totalAmount = 0;
    const clientNames = new Set();
    const providerNames = new Set();
    const employeeNames = new Set();
    const statuses = new Set();
    const labels = new Set();

    groupVisits.forEach((visit) => {
        const { visitMinutes, minutesPerUnit, billUnit, serviceAmount, billableStatus } = getVisitUnitsAndAmount(visit);
        totalMinutes += visitMinutes || 0;
        totalBillableMinutes += billableStatus ? billUnit * minutesPerUnit : 0;
        totalUnits += billUnit || 0;
        totalAmount += Number(serviceAmount) || 0;

        if (visit?.client) {
            clientNames.add(`${visit?.client?.firstName || ''} ${visit?.client?.lastName || ''}`.trim());
        } else {
            clientNames.add('Deleted Client');
        }

        if (visit?.provider) {
            providerNames.add(visit?.provider?.agencyName || 'Agency deleted');
        } else {
            providerNames.add('Agency deleted');
        }

        if (visit?.employee) {
            employeeNames.add(visit?.employee?.user?.name || 'Deleted Employee');
        } else {
            employeeNames.add('Deleted Employee');
        }

        statuses.add(visit?.status || 'complete');
        if (visit?.label || visit?.extraInfo?.label) {
            labels.add(visit?.label || visit?.extraInfo?.label);
        }

        if (visit?.ScheduleStartTime) {
            if (!earliestStart || dayjs(visit?.ScheduleStartTime).isBefore(dayjs(earliestStart))) {
                earliestStart = visit?.ScheduleStartTime;
            }
        }
        if (visit?.ScheduleEndTime) {
            if (!latestEnd || dayjs(visit?.ScheduleEndTime).isAfter(dayjs(latestEnd))) {
                latestEnd = visit?.ScheduleEndTime;
            }
        }
    });

    const toList = (setValue) => Array.from(setValue);
    const clientName = clientNames.size === 1 ? toList(clientNames)[0] : 'Multiple Clients';
    const providerName = providerNames.size === 1 ? toList(providerNames)[0] : 'Multiple Agencies';
    const employeeName = employeeNames.size === 1 ? toList(employeeNames)[0] : 'Multiple Employees';
    const status = statuses.size === 1 ? toList(statuses)[0] : 'multiple';
    const label = labels.size === 1 ? toList(labels)[0] : null;
    const serviceCode = getServiceCode(firstVisit);

    return {
        startDate: earliestStart ? dayjs(earliestStart).format('MM-DD-YYYY') : '--',
        serviceCode,
        earliestStart,
        latestEnd,
        totalMinutes,
        totalBillableMinutes,
        totalUnits,
        totalAmount,
        visitCount: groupVisits.length,
        clientName,
        providerName,
        employeeName,
        status,
        label,
    };
};

export const getCombinedVisitList = (visits = []) => {
    const groupedMap = new Map();
    const orderedKeys = [];

    // Only include verified visits for combining
    const { verifiedVisits, unverifiedVisits } = visits.reduce(
        (acc, v) => {
            // if (v?.status === 'verified' && v?.extraInfo?.label === 'verified') {
            if (v?.extraInfo?.label === 'verified') {
                acc.verifiedVisits.push(v);
            } else {
                acc.unverifiedVisits.push(v);
            }
            return acc;
        },
        { verifiedVisits: [], unverifiedVisits: [] }
    );
    verifiedVisits.forEach((visit, index) => {
        const startDate = dayjs(visit?.ScheduleStartTime).isValid()
            ? dayjs(visit?.ScheduleStartTime).format('YYYY-MM-DD')
            : 'unknown-date';

        const serviceCode = getServiceCode(visit);
        const modifier = getCombinedModifier(visit);
        // Only group if BOTH service code AND modifier match (or both have no modifier)
        const modKey = modifier;
        const rawKey = `${startDate}|${serviceCode}|${modKey}`;

        if (!groupedMap.has(rawKey)) {
            groupedMap.set(rawKey, []);
            orderedKeys.push(rawKey);
        }
        groupedMap.get(rawKey).push(visit);
    });
    const includedVisitIds = new Set();

    // Unverified visits always render as singles
    const unverifiedEntries = unverifiedVisits.map((visit) => {
        const visitId = visit?._id || visit?.id;
        includedVisitIds.add(visitId);
        return {
            type: 'single',
            key: visitId,
            visit,
        };
    });

    const combinedEntries = orderedKeys
        .map((key) => {
            let groupVisits = groupedMap.get(key) || [];
            // Remove duplicates: same service code + same modifier + overlapping times
            const deduped = [];
            for (const visit of groupVisits) {
                const isDuplicate = deduped.some(
                    (existing) =>
                        getServiceCode(existing) === getServiceCode(visit) &&
                        getCombinedModifier(existing) === getCombinedModifier(visit) &&
                        timesOverlap(existing, visit)
                );
                if (!isDuplicate) {
                    deduped.push(visit);
                }
            }
            groupVisits = deduped;

            if (groupVisits.length > 1) {
                groupVisits.forEach((visit) => {
                    const visitId = visit?._id || visit?.id;
                    includedVisitIds.add(visitId);
                });
                return {
                    type: 'combined',
                    key,
                    visits: groupVisits,
                    summary: buildCombinedVisitSummary(groupVisits),
                };
            }
            const singleVisit = groupVisits[0];
            if (!singleVisit) return null;
            const visitId = singleVisit?._id || singleVisit?.id;
            includedVisitIds.add(visitId);
            return {
                type: 'single',
                key: visitId,
                visit: singleVisit,
            };
        })
        .filter(Boolean);

    const missingVisits = verifiedVisits.filter((visit) => {
        const visitId = visit?._id || visit?.id;
        return !includedVisitIds.has(visitId);
    });

    const missingEntries = missingVisits.map((visit) => {
        const visitId = visit?._id || visit?.id;
        return {
            type: 'single',
            key: visitId,
            visit,
        };
    });

    const allEntries = [...combinedEntries, ...unverifiedEntries, ...missingEntries];

    // Sort all entries by visit date descending (most recent first)
    allEntries.sort((a, b) => {
        const aTime = dayjs(
            a.type === 'combined' ? a.summary?.ScheduleStartTime : a.visit?.ScheduleStartTime
        ).valueOf();
        const bTime = dayjs(
            b.type === 'combined' ? b.summary?.ScheduleStartTime : b.visit?.ScheduleStartTime
        ).valueOf();
        return bTime - aTime;
    });

    return allEntries;
};
